/**
 * Centralized Query Key Factory
 * 
 * Provides consistent, hierarchical query keys for React Query
 * Enables efficient cache invalidation and management
 */

export const queryKeys = {
  // Top-level entities
  reservations: {
    all: ['reservations'],
    lists: () => [...queryKeys.reservations.all, 'list'],
    list: (filters) => [...queryKeys.reservations.lists(), { filters }],
    details: () => [...queryKeys.reservations.all, 'detail'],
    detail: (id) => [...queryKeys.reservations.details(), id],
    // User-specific reservations
    byUser: (userId) => [...queryKeys.reservations.all, 'user', userId],
    byUserFiltered: (userId, filters) => [...queryKeys.reservations.byUser(userId), { filters }],
    // Venue-specific reservations
    byVenue: (venueId) => [...queryKeys.reservations.all, 'venue', venueId],
    byVenueFiltered: (venueId, filters) => [...queryKeys.reservations.byVenue(venueId), { filters }],
    // Team-specific reservations
    byTeam: (teamId) => [...queryKeys.reservations.all, 'team', teamId],
    byTeamFiltered: (teamId, filters) => [...queryKeys.reservations.byTeam(teamId), { filters }],
    // Time-based reservations
    upcoming: (userId) => [...queryKeys.reservations.all, 'upcoming', userId],
    past: (userId) => [...queryKeys.reservations.all, 'past', userId],
    // Availability checks
    availability: (venueId, date) => [...queryKeys.reservations.all, 'availability', venueId, date],
  },

  venues: {
    all: ['venues'],
    lists: () => [...queryKeys.venues.all, 'list'],
    list: (filters) => [...queryKeys.venues.lists(), { filters }],
    details: () => [...queryKeys.venues.all, 'detail'],
    detail: (id) => [...queryKeys.venues.details(), id],
    // Location-based
    nearby: (lat, lng, radius) => [...queryKeys.venues.all, 'nearby', { lat, lng, radius }],
    byCity: (city) => [...queryKeys.venues.all, 'city', city],
    byState: (state) => [...queryKeys.venues.all, 'state', state],
    // Availability
    available: (date, filters) => [...queryKeys.venues.all, 'available', date, { filters }],
  },

  teams: {
    _def: ['teams'],
    all: ['teams'],
    lists: () => [...queryKeys.teams.all, 'list'],
    list: (filters) => [...queryKeys.teams.lists(), { filters }],
    count: (filters) => [...queryKeys.teams.all, 'count', { filters }],
    details: () => [...queryKeys.teams.all, 'detail'],
    detail: (id) => [...queryKeys.teams.details(), id],
    // Search functionality
    searches: () => [...queryKeys.teams.all, 'search'],
    search: (query, options = {}) => [...queryKeys.teams.searches(), query, options],
    // User-specific
    byUser: (userId) => [...queryKeys.teams.all, 'user', userId],
    byManager: (userId) => [...queryKeys.teams.all, 'manager', userId],
    userManaged: (userId) => [...queryKeys.teams.all, 'user-managed', userId],
    // Tournament-specific
    byTournament: (tournamentId) => [...queryKeys.teams.all, 'tournament', tournamentId],
    // Statistics
    statistics: (teamId) => [...queryKeys.teams.all, 'statistics', teamId],
    // Reference data
    divisions: () => [...queryKeys.teams.all, 'divisions'],
    states: () => [...queryKeys.teams.all, 'states'],
    // Team manager access requests
    userPendingRequests: (userId) => [...queryKeys.teams.all, 'user-pending-requests', userId],
  },

  tournaments: {
    all: ['tournaments'],
    lists: () => [...queryKeys.tournaments.all, 'list'],
    list: (filters) => [...queryKeys.tournaments.lists(), { filters }],
    details: () => [...queryKeys.tournaments.all, 'detail'],
    detail: (id) => [...queryKeys.tournaments.details(), id],
    // Status-based
    active: () => [...queryKeys.tournaments.all, 'active'],
    upcoming: () => [...queryKeys.tournaments.all, 'upcoming'],
    past: () => [...queryKeys.tournaments.all, 'past'],
    // Team-related
    teamRegistrations: (teamId) => [...queryKeys.tournaments.all, 'team-registrations', teamId],
  },

  users: {
    all: ['users'],
    lists: () => [...queryKeys.users.all, 'list'],
    list: (filters) => [...queryKeys.users.lists(), { filters }],
    details: () => [...queryKeys.users.all, 'detail'],
    detail: (id) => [...queryKeys.users.details(), id],
    // Role-based
    byRole: (role) => [...queryKeys.users.all, 'role', role],
    pending: () => [...queryKeys.users.all, 'pending'],
    profile: (userId) => [...queryKeys.users.all, 'profile', userId],
  },

  contact: {
    all: ['contact'],
    lists: () => [...queryKeys.contact.all, 'list'],
    list: (filters) => [...queryKeys.contact.lists(), { filters }],
    details: () => [...queryKeys.contact.all, 'detail'],
    detail: (id) => [...queryKeys.contact.details(), id],
    // User-specific
    byUser: (userId) => [...queryKeys.contact.all, 'user', userId],
    // Status-based
    byStatus: (status) => [...queryKeys.contact.all, 'status', status],
    // Statistics
    stats: () => [...queryKeys.contact.all, 'stats'],
  },

  notifications: {
    all: ['notifications'],
    lists: () => [...queryKeys.notifications.all, 'list'],
    list: (filters) => [...queryKeys.notifications.lists(), { filters }],
    details: () => [...queryKeys.notifications.all, 'detail'],
    detail: (id) => [...queryKeys.notifications.details(), id],
    // Unread count
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'],
    // User-specific
    byUser: (userId) => [...queryKeys.notifications.all, 'user', userId],
    // Status-based
    unread: () => [...queryKeys.notifications.all, 'unread'],
    archived: () => [...queryKeys.notifications.all, 'archived'],
  },

  emailSettings: {
    all: ['emailSettings'],
    current: () => [...queryKeys.emailSettings.all, 'current'],
  },

  // Derived data
  dashboard: {
    overview: (userId) => ['dashboard', 'overview', userId],
    stats: (userId) => ['dashboard', 'stats', userId],
    recentActivity: (userId) => ['dashboard', 'recent-activity', userId],
  },

  // Real-time data
  availability: {
    venue: (venueId, date) => ['availability', 'venue', venueId, date],
    time: (venueId, date, timeSlot) => ['availability', 'time', venueId, date, timeSlot],
  },
};

/**
 * Helper functions for query key management
 */
export const queryKeyHelpers = {
  /**
   * Invalidate all queries for a specific entity type
   * @param {QueryClient} queryClient 
   * @param {string} entityType - e.g., 'reservations', 'venues'
   */
  invalidateEntity: (queryClient, entityType) => {
    return queryClient.invalidateQueries({ queryKey: queryKeys[entityType].all });
  },

  /**
   * Invalidate specific user's data across all entities
   * @param {QueryClient} queryClient 
   * @param {string} userId 
   */
  invalidateUserData: (queryClient, userId) => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.reservations.byUser(userId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.byUser(userId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.overview(userId) }),
    ]);
  },

  /**
   * Remove specific entity from cache
   * @param {QueryClient} queryClient 
   * @param {string} entityType 
   * @param {string} id 
   */
  removeEntity: (queryClient, entityType, id) => {
    return queryClient.removeQueries({ queryKey: queryKeys[entityType].detail(id) });
  },

  /**
   * Prefetch related data for an entity
   * @param {QueryClient} queryClient 
   * @param {string} entityType 
   * @param {string} id 
   * @param {Object} relatedQueries 
   */
  prefetchRelated: (queryClient, entityType, id, relatedQueries) => {
    return Promise.all(
      relatedQueries.map(query => queryClient.prefetchQuery(query))
    );
  },
};

export default queryKeys;